/**
 * API Client - Unified API request handling
 * Works with React Query to provide consistent error handling and request formatting
 * Supports language headers for i18n
 */

// 從 i18n 模組獲取當前語言設置
import { getLanguage } from './i18n';

const API_BASE = '/api'; // 基礎 API 路徑

class ApiError extends Error {
  constructor(message, status, data) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

const defaultHeaders = () => ({
  'Accept': 'application/json',
  'Accept-Language': getLanguage(),
});

async function handleResponse(response) {
  const data = await response.json().catch(() => ({}));
  
  if (!response.ok) {
    throw new ApiError(
      data.message || `HTTP ${response.status}`,
      response.status,
      data
    );
  }
  
  return data;
}

export const apiClient = {
  async get(endpoint, options = {}) {
    const url = new URL(`${API_BASE}${endpoint}`, window.location.origin);
    if (options.params) {
      Object.entries(options.params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          url.searchParams.append(key, value);
        }
      });
    }
    
    const response = await fetch(url.toString(), {
      method: 'GET',
      credentials: 'include',
      headers: {
        ...defaultHeaders(),
        ...options.headers
      },
      signal: options.signal
    });
    
    return handleResponse(response);
  },

  async post(endpoint, body, options = {}) {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...defaultHeaders(),
        ...options.headers
      },
      body: body ? JSON.stringify(body) : undefined,
      signal: options.signal
    });
    
    return handleResponse(response);
  },

  async put(endpoint, body, options = {}) {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...defaultHeaders(),
        ...options.headers
      },
      body: body ? JSON.stringify(body) : undefined,
      signal: options.signal
    });
    
    return handleResponse(response);
  },

  async delete(endpoint, options = {}) {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        ...defaultHeaders(),
        ...options.headers
      },
      signal: options.signal
    });
    
    return handleResponse(response);
  }
};

export { ApiError };
export default apiClient;
